import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { getProviderSlotLocks, type SlotLock } from '@/services/bookingApi';
import { SlotLockProgressBar } from './SlotLockProgressBar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

export const InProgressReservations: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const [locks, setLocks] = useState<SlotLock[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchLocks();
    const interval = setInterval(fetchLocks, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchLocks = async () => {
    try {
      const data = await getProviderSlotLocks();
      setLocks(data || []);
    } catch (err) {
      console.error('Failed to load active slot locks:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return null;
  }

  if (locks.length === 0) {
    return null;
  }

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-medium">
          <Clock className="w-4 h-4 text-primary" />
          {t('bookings.inProgressTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {locks.map((lock) => (
          <div
            key={lock.id}
            className="border border-border rounded-lg p-3 bg-muted/50"
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <span className="font-medium text-foreground">
                  {formatDate(lock.date)}
                </span>
                <span className="text-muted-foreground ml-2">
                  {formatTime(lock.time)}
                </span>
              </div>
            </div>
            <SlotLockProgressBar
              elapsed_seconds={lock.progress.elapsed_seconds}
              remaining_seconds={lock.progress.remaining_seconds}
              percentage={lock.progress.percentage}
              status={lock.progress.status}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
